import { isDuplicateSignup } from "../shared/signup-duplicate";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Dialog, Field } from "@carepass/ui";
import { partnerJoinStore, runtimeConfig } from "../../app/runtime";
import { partnerApi } from "../shared/api";
import type { PartnerMobilePayment, PartnerTerm } from "../shared/types";
import { formatWonAmount as money } from "../shared/won";
import { AgreementGroup } from "./components/AgreementGroup";
import { checkoutUrlForCurrentDevice } from "../shared/checkout-url";
import { PartnerIdentityNumber } from "./components/PartnerIdentityNumber";
import { CarrierChoices } from "../shared/CarrierChoices";
import { usePartnerReturn } from "./usePartnerReturn";
import payletterBenefitUrl from "../../assets/brand/payletter-exclusive-benefit.png";
import couponPromotionUrl from "../../assets/partner/gs25-coupon-promotion-5000.png";
import "../signup/DirectSignupPage.css";
import "./PartnerJoinPage.css";

type Stage = "payment" | "completed" | "canceled";

// Attempt timestamps include an offset, so expiry is independent of the device timezone.
const paymentTime = (value: string) => Date.parse(value);

function queryInput() {
  const search = new URLSearchParams(window.location.search);
  const channel = search.get("channel") ?? "";
  const banner = search.get("banner_cd") ?? "";
  const returnUrl = search.get("return_url") ?? "";
  return { channel, banner, returnUrl, trid: search.get("trid") ?? "", callbackUrl: search.get("callback_url") ?? "", customParameter: search.get("custom_parameter") ?? undefined };
}

export function PartnerJoinPage() {
  const queryClient = useQueryClient();
  const input = useMemo(queryInput, []);
  const persisted = partnerJoinStore.get();
  const [sessionToken, setSessionToken] = useState(() => new URLSearchParams(window.location.search).get("session") ?? (input.channel && input.banner && input.returnUrl ? undefined : persisted?.sessionToken));
  const [stage, setStage] = useState<Stage>("payment");
  const [agreements, setAgreements] = useState<Readonly<Record<number, boolean>>>({});
  const [openTerm, setOpenTerm] = useState<PartnerTerm>();
  const termContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const body = termContentRef.current?.parentElement;
    if (body && openTerm) body.scrollTop = 0;
  }, [openTerm]);
  const [contact, setContact] = useState({ phoneNumber: "", telecomCode: "", mvnoNetwork: "" });
  const [error, setError] = useState<string>();
  const [socialNumber, setSocialNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [bannerHidden, setBannerHidden] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const resetAfterDuplicate = useRef(false);
  const restoredContact = useRef(false);
  const [paymentAttemptToken, setPaymentAttemptToken] = useState<string>();
  const requestKey = useRef(crypto.randomUUID());
  const authenticatedIdentity = useRef(false);
  const [now, setNow] = useState(Date.now);

  const session = useQuery({
    queryKey: ["partner", "session", sessionToken, input.channel, input.banner],
    enabled: Boolean(sessionToken || (input.channel && input.banner && input.returnUrl)),
    queryFn: async () => {
      if (sessionToken) return partnerApi.status(sessionToken);
      const opened = await partnerApi.open({
        channel: input.channel,
        trid: input.trid,
        callback_url: input.callbackUrl,
        banner_cd: input.banner,
        return_url: input.returnUrl,
        custom_parameter: input.customParameter,
      });
      partnerJoinStore.set({ sessionToken: opened.sessionToken, expiresAt: opened.expiresAt });
      const url = new URL(window.location.href);
      url.searchParams.set("session", opened.sessionToken);
      window.history.replaceState(window.history.state, "", url);
      setSessionToken(opened.sessionToken);
      return opened;
    },
    retry: false,
  });
  const terms = useQuery({
    queryKey: ["partner", "terms", sessionToken],
    enabled: Boolean(sessionToken && session.data && !["COMPLETED", "CANCELED", "EXPIRED"].includes(session.data.status)),
    queryFn: () => partnerApi.terms(sessionToken!),
  });
  const mobilePayment = useQuery({
    queryKey: ["partner", "mobile-payment", sessionToken, paymentAttemptToken],
    enabled: Boolean(sessionToken && paymentAttemptToken && stage === "payment"),
    queryFn: () => partnerApi.mobilePayment(sessionToken!, paymentAttemptToken!),
    retry: false,
    refetchInterval: (query) => ["REQUESTING", "RESENDING", "APPROVING", "AUTH_UNKNOWN", "PAYMENT_UNKNOWN"].includes(query.state.data?.status ?? "") ? 2500 : false,
  });
  const payment = mobilePayment.data;
  const expired = Boolean(payment && paymentTime(payment.expiresAt) <= now);
  const paymentUncertain = Boolean(payment && ["AUTH_UNKNOWN", "PAYMENT_UNKNOWN"].includes(payment.status));
  const paymentProcessing = Boolean(payment && ["REQUESTING", "RESENDING", "APPROVING"].includes(payment.status));
  const legacyPending = session.data?.status === "PAYMENT_PENDING" && !paymentAttemptToken && !session.data.mobilePaymentAttemptToken;
  const legacyPaymentCompleted = session.data?.status === "PAYMENT_COMPLETED" && !paymentAttemptToken && !session.data.mobilePaymentAttemptToken;

  useEffect(() => {
    if (session.data?.mobilePaymentAttemptToken) setPaymentAttemptToken(session.data.mobilePaymentAttemptToken);
  }, [session.data?.mobilePaymentAttemptToken]);
  useEffect(() => {
    if (stage !== "payment") { setSocialNumber(""); setVerificationCode(""); return; }
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    const clearSensitive = () => { setSocialNumber(""); setVerificationCode(""); };
    window.addEventListener("pagehide", clearSensitive);
    return () => { window.clearInterval(timer); window.removeEventListener("pagehide", clearSensitive); };
  }, [stage]);
  useEffect(() => {
    if (expired || payment?.status === "FAILED" || payment?.status === "APPROVED" || payment?.status === "COMPLETED" || paymentUncertain) {
      setSocialNumber(""); setVerificationCode("");
    }
    if ((expired && payment?.status === "OTP_SENT") || payment?.status === "FAILED") requestKey.current = crypto.randomUUID();
    if (payment?.status === "COMPLETED") { partnerJoinStore.clear(); setStage("completed"); void session.refetch(); }
  }, [expired, payment?.status, paymentUncertain]);

  useEffect(() => {
    if (!session.data?.status) return;
    if (session.data.status === "COMPLETED") setStage("completed");
    if (["CANCELED", "FAILED", "EXPIRED"].includes(session.data.status)) setStage("canceled");

    if (["CONTACT_RECORDED", "PAYMENT_PENDING", "PAYMENT_COMPLETED"].includes(session.data.status)) setStage("payment");
  }, [session.data?.status]);
  useEffect(() => {
    if (session.data?.status !== "PAYMENT_PENDING") return;
    const timer = window.setInterval(() => { void session.refetch(); }, 2500);
    return () => window.clearInterval(timer);
  }, [session.data?.status, session.refetch]);

  useEffect(() => {
    if (!restoredContact.current && session.data?.contact) {
      setContact({ phoneNumber: session.data.contact.phoneNumber, telecomCode: session.data.contact.telecomCode, mvnoNetwork: session.data.contact.mvnoNetwork ?? "" });
      setAgreements(Object.fromEntries((session.data.agreements ?? []).map(item => [item.termVersionId, item.agreed])));
      restoredContact.current = true;
    }
  }, [session.data?.contact, session.data?.agreements]);
  const browserReturn = usePartnerReturn(stage, sessionToken);
  useEffect(() => {
    if (browserReturn.error) setError(browserReturn.error.message);
  }, [browserReturn.error]);
  const returnToPartner = () => {
    if (!sessionToken || browserReturn.isPending) return;
    setError(undefined);
    browserReturn.mutate();
  };
  const complete = useMutation({
    mutationFn: () => partnerApi.complete(sessionToken!),
    onSuccess: async (result) => {
      const checkoutUrl = checkoutUrlForCurrentDevice(result);
      if (checkoutUrl) { window.location.assign(checkoutUrl); return; }
      if (result.status !== "COMPLETED") { await session.refetch(); return; }
      partnerJoinStore.clear(); setError(undefined); setStage("completed");
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "가입 결과를 확인하지 못했습니다."),
  });
  const receivePayment = (result: PartnerMobilePayment) => {
    setPaymentAttemptToken(result.paymentAttemptToken);
    queryClient.setQueryData(["partner", "mobile-payment", sessionToken, result.paymentAttemptToken], result);
    setError(undefined); setVerificationCode(""); setNow(Date.now());
    void session.refetch();
  };
  const showDuplicate = (cause: unknown) => {
    if (!isDuplicateSignup(cause) || payment?.status === "APPROVED" || payment?.status === "COMPLETED") return false;
    setError(undefined); setSocialNumber(""); setVerificationCode(""); setDuplicateOpen(true); return true;
  };
  const mobileRequest = useMutation({
    gcTime: 0,
    mutationFn: (consents: Readonly<Record<number, boolean>>) => partnerApi.requestMobilePayment(sessionToken!, { requestKey: requestKey.current, socialNumber, phoneNumber: contact.phoneNumber.replace(/\D/g, ""), telecomCode: contact.telecomCode, ...(contact.mvnoNetwork ? { mvnoNetwork: contact.mvnoNetwork } : {}), agreements: (terms.data ?? []).map(term => ({ termVersionId: term.termVersionId, agreed: Boolean(consents[term.termVersionId]) })) }),
    onSuccess: result => { authenticatedIdentity.current = result.status === "OTP_SENT"; receivePayment(result); },
    onError: (cause) => { setSocialNumber(""); if (!showDuplicate(cause)) setError(cause instanceof Error ? cause.message : "결제 인증번호를 요청하지 못했습니다."); void session.refetch(); },
  });
  const mobileConfirm = useMutation({
    gcTime: 0,
    mutationFn: () => partnerApi.confirmMobilePayment(sessionToken!, paymentAttemptToken!, { verificationCode, socialNumber }),
    onSuccess: receivePayment,
    onError: (cause) => { setVerificationCode(""); if (!showDuplicate(cause)) setError(cause instanceof Error ? cause.message : "결제를 승인하지 못했습니다."); void mobilePayment.refetch(); },
  });
  const mobileResend = useMutation({
    gcTime: 0,
    mutationFn: () => partnerApi.resendMobilePayment(sessionToken!, paymentAttemptToken!, { socialNumber }),
    onSuccess: receivePayment,
    onError: (cause) => { setError(cause instanceof Error ? cause.message : "인증번호를 다시 보내지 못했습니다."); void mobilePayment.refetch(); },
  });
  const mobileComplete = useMutation({
    mutationFn: () => partnerApi.completeMobilePayment(sessionToken!, paymentAttemptToken!),
    onSuccess: receivePayment,
    onError: (cause) => { setError(cause instanceof Error ? cause.message : "가입 완료를 확인하지 못했습니다."); void mobilePayment.refetch(); },
  });
  const invalidate = useMutation({
    mutationFn: () => partnerApi.invalidateMobilePayment(sessionToken!, paymentAttemptToken!),
    onSuccess: result => {
      if (result.status !== "INVALIDATED") { receivePayment(result); return; }
      authenticatedIdentity.current = false;
      requestKey.current = crypto.randomUUID();
      setVerificationCode(""); setSocialNumber("");
      receivePayment(result);
      if (resetAfterDuplicate.current) { resetAfterDuplicate.current = false; setContact(current => ({ ...current, phoneNumber: "" })); setDuplicateOpen(false); }
    },
    onError: cause => { resetAfterDuplicate.current = false; setError(cause instanceof Error ? cause.message : "인증 상태를 변경하지 못했습니다."); void mobilePayment.refetch(); },
  });
  const correctDuplicatePhone = () => {
    if (payment?.status === "OTP_SENT") { resetAfterDuplicate.current = true; invalidate.mutate(); return; }
    setContact(current => ({ ...current, phoneNumber: "" })); setError(undefined); setDuplicateOpen(false);
  };
  const duplicateReturn = useMutation({
    mutationFn: () => partnerApi.duplicateReturn(sessionToken!, contact.phoneNumber.replace(/\D/g, "")),
    onSuccess: () => {
      partnerJoinStore.clear(); setSocialNumber(""); setVerificationCode("");
      setDuplicateOpen(false); setStage("canceled");
    },
    onError: cause => setError(cause instanceof Error ? cause.message : "복귀하지 못했습니다. 잠시 후 다시 시도해 주세요."),
  });
  const mobileBusy = mobileRequest.isPending || mobileConfirm.isPending || mobileResend.isPending || mobileComplete.isPending || invalidate.isPending || duplicateReturn.isPending;
  const cancel = useMutation({
    mutationFn: () => partnerApi.cancel(sessionToken!),
    onSuccess: (result) => {
      setCloseOpen(false);
      if (result.status === "COMPLETED") { setStage("completed"); return; }
      if (!["CANCELED", "FAILED", "EXPIRED"].includes(result.status)) { setError("결제 결과를 확인하고 있습니다. 상태 확인 후 다시 시도해 주세요."); void session.refetch(); return; }
      partnerJoinStore.clear(); setContact({ phoneNumber: "", telecomCode: "", mvnoNetwork: "" }); setSocialNumber(""); setVerificationCode(""); setAgreements({}); setStage("canceled");
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "가입을 취소하지 못했습니다."),
  });

  const agreementItems = (terms.data ?? []).map((term) => ({
    termVersionId: term.termVersionId,
    title: term.title,
    required: term.required,
    agreed: Boolean(agreements[term.termVersionId]),
  }));
  const otpActive = payment?.status === "OTP_SENT" && !expired;
  const validIdentity = /^\d{6}[1-8]$/.test(socialNumber);
  const validContact = /^01\d{8,9}$/.test(contact.phoneNumber.replace(/\D/g, "")) && Boolean(contact.telecomCode) && (contact.telecomCode !== "MVNO" || Boolean(contact.mvnoNetwork));
  const canRequest = validIdentity && validContact && Boolean(terms.data?.length) && !terms.isError && !mobileBusy && stage === "payment";
  const locked = stage !== "payment" || mobileBusy || Boolean(otpActive);
  const remainingSeconds = payment ? Math.max(0, Math.ceil((paymentTime(payment.expiresAt) - now) / 1000)) : 0;
  const cooldown = payment ? Math.max(0, Math.ceil((paymentTime(payment.resendAvailableAt) - now) / 1000)) : 0;
  const plan = session.data?.presentation;
  const bannerImageUrl = plan?.signupImageUrl?.startsWith("/api/")
    ? `${runtimeConfig.apiBaseUrl.replace(/\/api$/, "")}${plan.signupImageUrl}`
    : plan?.signupImageUrl;

  if (!sessionToken && (!input.channel || !input.banner || !input.returnUrl)) {
    return (
      <main className="partner-flow member-signup member-partner-signup partner-onepage">
        <header className="member-signup__header"><span className="partner-onepage__brand">케어패스</span><span>제휴 가입</span></header>
        <section className="member-signup__card">
          <Alert persistent variant="warning" title="유효한 가입 링크가 필요합니다">
            제휴사에서 받은 가입 링크를 다시 열어 주세요. 일반 가입은 CarePass 홈페이지에서 시작할 수 있습니다.
          </Alert>
          <Button variant="secondary" onClick={() => window.history.back()}>이전 화면으로</Button>
        </section>
      </main>
    );
  }

  if (session.isPending || (session.data?.status === "OPENED" && terms.isPending)) {
    return <main className="partner-flow member-signup member-partner-signup partner-onepage"><div className="partner-flow__loading" role="status">가입 정보를 불러오는 중입니다.</div></main>;
  }
  if (session.isError || (session.data?.status === "OPENED" && terms.isError) || !plan) {
    return (
      <main className="partner-flow member-signup member-partner-signup partner-onepage"><section className="member-signup__card"><Alert persistent variant="danger" title="가입 정보를 불러오지 못했습니다">가입 링크가 만료되었거나 서버에 연결할 수 없습니다.</Alert></section></main>
    );
  }

  return (
    <main className="partner-flow member-signup member-partner-signup partner-onepage">
      <div className="partner-onepage__surface">
        <header className="partner-onepage__header"><span className="partner-onepage__brand">케어패스</span>{stage === "payment" && <Button variant="quiet" aria-label="닫기" onClick={() => setCloseOpen(true)} disabled={mobileBusy || paymentProcessing}><span aria-hidden="true">×</span></Button>}</header>
        <div className="partner-onepage__partner-brand"><img src={payletterBenefitUrl} width={200} height={25} alt="payletter 단독 혜택" /></div>
        <div className="partner-onepage__hero">
        {!bannerHidden && bannerImageUrl && <div className="partner-onepage__banner">
          <img src={bannerImageUrl} alt={plan.signupImageAlt || "CarePass 제휴 이벤트"} onError={() => setBannerHidden(true)} />
          <button type="button" aria-label="광고 배너 닫기" onClick={() => setBannerHidden(true)}>×</button>
        </div>}
        {stage !== "canceled" && <figure className="partner-onepage__promotion"><img src={couponPromotionUrl} width={1920} height={893} alt="GS25 쿠폰 5천원 받기. 5,000원 쿠폰이 입력하신 휴대폰 번호로 발송됩니다. 휴대폰번호 입력 후 완료하세요." /></figure>}
        </div>
        <div className="partner-onepage__body">
          {browserReturn.isPending && <Alert persistent title="가입 결과 전달을 확인하고 있습니다">제휴사의 정상 수신이 확인되면 원래 화면으로 이동합니다.</Alert>}
          {error && stage !== "completed" && !closeOpen && !duplicateOpen && <Alert persistent variant="danger" title="진행하지 못했습니다">{error}</Alert>}
          {stage !== "canceled" && <>
            <header className="partner-onepage__intro"><h1>월세·보험료도 카드결제 하고<br />안심 케어 서비스도 무상 지원 받으세요.</h1><p>* 서비스 이용료 : 부가세 포함 <span>월 {money(plan.monthlyFee)}</span></p></header>
            {legacyPaymentCompleted ? <div className="partner-flow__actions">
              <Alert persistent title="기존 결제가 완료되었습니다">가입 완료를 확인해 주세요. 결제는 다시 청구하지 않습니다.</Alert>
              <Button size="large" isLoading={complete.isPending} onClick={() => complete.mutate()}>가입 완료 이어가기</Button>
            </div> : legacyPending ? <div className="partner-flow__actions">
              <Alert persistent title="기존 결제 결과를 확인하고 있습니다">이미 시작한 결제창에서 이어서 진행해 주세요. 새 결제를 중복 요청하지 않습니다.</Alert>
              <Button size="large" isLoading={complete.isPending} onClick={() => complete.mutate()}>결제창 다시 열기</Button>
            </div> : mobilePayment.isError ? <div className="partner-flow__actions">
              <Alert persistent variant="danger" title="결제 상태를 확인하지 못했습니다">중복 결제를 막기 위해 현재 결제 결과를 먼저 확인해 주세요.</Alert>
              <Button onClick={() => void mobilePayment.refetch()}>결제 상태 다시 확인</Button>
            </div> : paymentAttemptToken && mobilePayment.isPending ? <p role="status">결제 상태를 불러오는 중입니다.</p> : payment?.status === "APPROVED" ? <div className="partner-flow__actions">
              <Alert persistent variant="warning" title="결제는 승인되었지만 가입 저장이 완료되지 않았습니다">
                {payment.signupFailureMessage && <p>{payment.signupFailureMessage}</p>}
                <p>{payment.signupRetryable === false ? "다시 결제하지 마세요. 승인된 결제와 가입 상태는 고객센터에서 확인해 드립니다." : "아래 버튼으로 가입 완료를 다시 시도해 주세요. 결제는 다시 청구하지 않습니다."}</p>
              </Alert>
              {payment.signupRetryable === false
                ? <a className="cp-button cp-button--primary cp-button--large partner-onepage__support" href="tel:15225663">고객센터 문의 · 1522-5663</a>
                : <Button size="large" isLoading={mobileComplete.isPending} disabled={mobileBusy} onClick={() => mobileComplete.mutate()}>가입 완료 다시 시도</Button>}
            </div> : paymentProcessing || paymentUncertain ? <div className="partner-flow__actions">
              <Alert persistent title={paymentUncertain ? "결제 결과를 확인해야 합니다" : "결제를 처리하고 있습니다"}>{paymentUncertain ? "결과가 확정되기 전에는 다시 결제하지 마세요. 결과가 계속 확인되지 않으면 고객센터로 문의해 주세요." : "가입이 완료되면 안내 후 원래 화면으로 이동합니다."}</Alert>
              <Button variant="secondary" onClick={() => void mobilePayment.refetch()}>결제 상태 확인</Button>
            </div> : <form className="partner-onepage__form" onSubmit={event => { event.preventDefault(); if (otpActive && validIdentity && /^\d{6}$/.test(verificationCode) && !mobileBusy) mobileConfirm.mutate(); }}>
              {payment?.status === "FAILED" && <Alert persistent variant="danger" title="결제하지 못했습니다">{payment.resultMessage || "입력 정보를 확인한 뒤 다시 요청해 주세요."}</Alert>}
              {otpActive && payment.resultCode && payment.resultCode !== "0000" && <Alert persistent variant="danger" title="결제를 승인하지 못했습니다">{payment.resultMessage || "인증번호를 확인한 뒤 다시 입력해 주세요."}</Alert>}
              {expired && <Alert persistent variant="warning" title="인증 시간이 만료되었습니다">주민번호 앞 7자리를 다시 입력하고 새 인증번호를 요청해 주세요.</Alert>}
              <CarrierChoices presentation="partner" value={contact.telecomCode} disabled={locked} onChange={telecomCode => setContact(current => ({ ...current, telecomCode, mvnoNetwork: "" }))} />
              {contact.telecomCode === "MVNO" && <Field label="알뜰폰 통신망" required control="select" disabled={locked} value={contact.mvnoNetwork} onChange={event => { const mvnoNetwork = event.currentTarget.value; setContact(current => ({ ...current, mvnoNetwork })); }}><option value="">선택해 주세요</option><option value="SKT">SKT 알뜰폰</option><option value="KT">KT 알뜰폰</option><option value="LGU">LG U+ 알뜰폰</option></Field>}
              <PartnerIdentityNumber value={socialNumber} onChange={setSocialNumber} disabled={mobileBusy || (otpActive && authenticatedIdentity.current)} />
              <div className="partner-onepage__phone">
                <Field id="partner-mobile-number" label="휴대폰 번호" type="tel" format="phone" required autoComplete="tel" disabled={locked} placeholder="휴대폰 번호를 입력하세요." value={contact.phoneNumber} onChange={event => { const phoneNumber = event.currentTarget.value; setContact(current => ({ ...current, phoneNumber })); }} />
              </div>
              <Button className="partner-onepage__join partner-onepage__request" type="button" variant="primary" isLoading={mobileRequest.isPending || mobileResend.isPending} disabled={otpActive ? mobileBusy || !validIdentity || cooldown > 0 : !canRequest} onClick={() => {
                if (otpActive) { mobileResend.mutate(); return; }
                if (!canRequest) return;
                const consents = Object.fromEntries((terms.data ?? []).map(term => [term.termVersionId, true]));
                setAgreements(consents);
                mobileRequest.mutate(consents);
              }}>{otpActive ? cooldown > 0 ? `${cooldown}초 후 재요청` : "인증 재요청" : "약관 동의 후 인증 요청"}</Button>
              {!otpActive && <p className="partner-onepage__hint partner-onepage__sr-only">약관 동의 후 인증 요청을 누르면 아래 약관 전체에 동의하고 인증번호를 요청합니다.</p>}
              {otpActive && <section className="partner-onepage__otp" aria-label="결제 인증">
                {otpActive && <p role="status">{payment.mode === "SANDBOX" ? `샌드박스 테스트입니다. 문자는 발송되지 않습니다. 인증번호 ${payment.sandboxVerificationCode ?? "123456"}을 입력해 주세요.` : "입력하신 휴대폰으로 결제 인증번호를 보냈습니다."}</p>}
                <span className="partner-onepage__sr-only">남은 인증 시간 {otpActive ? `${Math.floor(remainingSeconds / 60)}분 ${remainingSeconds % 60}초` : "인증번호 요청 전"}</span>
                <div className="partner-onepage__input-action"><Field label="결제 인증번호" autoFocus={otpActive && validIdentity} inputMode="numeric" autoComplete="one-time-code" required maxLength={6} placeholder="인증번호" trailingAdornment={<span className="partner-onepage__timer">{otpActive ? `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")}` : "00:00"}</span>} value={verificationCode} disabled={!otpActive || mobileBusy} onChange={event => setVerificationCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))} /><Button type="submit" isLoading={mobileConfirm.isPending} disabled={!otpActive || mobileBusy || !validIdentity || !/^\d{6}$/.test(verificationCode)}>확인</Button></div>
              </section>}
              {otpActive && <Button type="button" variant="quiet" disabled={mobileBusy} isLoading={invalidate.isPending} onClick={() => invalidate.mutate()}>정보 수정</Button>}
              {terms.isPending ? <p role="status">약관을 불러오는 중입니다.</p> : terms.isError ? <Alert persistent variant="danger" title="약관을 불러오지 못했습니다"><Button variant="quiet" onClick={() => void terms.refetch()}>다시 불러오기</Button></Alert> : <AgreementGroup allLabel="약관 전체 동의하기" allNote={false} showRequirement={false} presentation="grouped" items={agreementItems} readOnly={locked} onAllChange={agreed => setAgreements(Object.fromEntries(agreementItems.map(item => [item.termVersionId, agreed])))} onItemChange={(id, agreed) => setAgreements(current => ({ ...current, [id]: agreed }))} onOpenTerm={id => setOpenTerm(terms.data?.find(term => term.termVersionId === id))} />}
              <Button className="partner-onepage__join" type="submit" isLoading={mobileConfirm.isPending} disabled={!otpActive || mobileBusy || !validIdentity || !/^\d{6}$/.test(verificationCode)}>케어패스유료가입</Button>
              {otpActive && <p className="partner-onepage__hint">확인 또는 유료가입을 누르면 {money(plan.monthlyFee)}이 결제되고 가입이 완료됩니다.</p>}
            </form>}
          </>}

          {stage === "canceled" && <section className="partner-flow__result"><h1>가입이 종료되었습니다</h1><p>원래 화면으로 돌아가 다시 시작해 주세요.</p><Button size="large" isLoading={browserReturn.isPending} onClick={returnToPartner}>원래 화면으로 돌아가기</Button></section>}
          <div className="partner-onepage__legal">
          <aside className="partner-onepage__notices">
            <p className="partner-onepage__fee">매월 부가세 포함 {money(plan.monthlyFee)}<br />본 서비스는 KG파이낸셜 휴대폰 소액결제를 통해 매월 통신요금에 합산 청구됩니다.</p>
            <h2>이벤트 유의사항</h2>
            <ul><li>지급대상 : 케어패스 가입 후 30일 이상 가입 유지자</li><li>쿠폰 지급일 : 매월 15일 (공휴일인 경우 다음 영업일)<ul><li>3월 10일 가입자 중 30일 이상 가입 유지자 : 4월 15일 경품 지급</li><li>3월 16일 가입자 중 30일 이상 가입 유지자 : 5월 15일 경품 지급</li></ul></li><li>고객 부주의(휴대폰 번호 변경, 입력 오류, 누락 등)로 인한 경품 미수신에 대해서는 당사가 책임지지 않습니다.</li><li>비정상, 불법적인 방법으로 이벤트에 참여한 경우, 당첨자 선정에서 제외되거나 당첨이 취소될 수 있습니다.</li><li>본 이벤트는 제공업체 사정에 따라 별도의 고지 없이 변경 또는 중단될 수 있습니다.</li><li>경품으로 지급된 쿠폰은 기한 내 미사용 시 이용이 제한되며 이에 대한 별도 보상은 제공되지 않습니다.</li><li>본 이벤트는 휴대폰 번호 기준으로 1회만 참여 가능합니다.</li></ul>
            <h2>서비스 안내사항</h2><ul><li>본 서비스는 통신요금에 부가세 포함 매월 {money(plan.monthlyFee)}이 부과됩니다.</li><li>본 서비스는 주식회사 티에프솔루션이 운영합니다. (고객센터 : 1522-5663)</li><li>본 서비스는 청약철회가 불가능한 상품입니다.</li><li>홈페이지 주소 : <a href="https://carepass.co.kr/">https://carepass.co.kr/</a></li></ul>
          </aside>
          <footer className="partner-onepage__footer"><p>- 전화번호 : <a href="tel:15225663">1522-5663</a>, 이메일 : <a href="mailto:info@tfsolution.co.kr">info@tfsolution.co.kr</a></p><p>- 사업자등록번호 : 334-88-03776 / 통신판매업신고 : 제 2026-성남분당A-0454호</p></footer>
          </div>
        </div>
      </div>
      {stage === "completed" && <Dialog className="partner-onepage__dialog partner-onepage__dialog--completed" open closeDisabled onClose={() => {}} title="가입 완료"
        primaryAction={browserReturn.isError ? <Button onClick={returnToPartner}>돌아가기 다시 시도</Button> : undefined}>
        <p>입력하신 휴대폰번호로 케어패스 가입이 완료되었습니다.</p>
        <p>30일 가입 유지 시 휴대폰번호로 GS25 쿠폰이 발송됩니다.</p>
        <p className="partner-onepage__completion-status" role="status">{browserReturn.isPending ? "제휴사에 가입 결과를 전달하고 있습니다. 잠시 기다려 주세요." : browserReturn.isError ? "가입 결과 전달을 확인하지 못했습니다. 안내를 확인해 주세요." : "가입 결과 전달이 확인되면 원래 화면으로 이동합니다."}</p>
        {browserReturn.isError && <Alert persistent variant="danger" title="진행하지 못했습니다">{error}</Alert>}
      </Dialog>}
      <Dialog className="partner-onepage__dialog" open={duplicateOpen} closeDisabled={mobileBusy} onClose={correctDuplicatePhone} title="이미 가입된 정보입니다." description="입력하신 휴대폰 번호로 가입된 내역이 있습니다. 추가 결제는 진행되지 않습니다."
        primaryAction={<Button isLoading={duplicateReturn.isPending} disabled={mobileBusy} onClick={() => duplicateReturn.mutate()}>확인하고 돌아가기</Button>}
        secondaryAction={<Button variant="secondary" isLoading={invalidate.isPending} disabled={mobileBusy} onClick={correctDuplicatePhone}>전화번호 다시 입력</Button>}>
        {duplicateOpen && error && <Alert persistent variant="danger" title="진행하지 못했습니다">{error}</Alert>}
      </Dialog>
      <Dialog className="partner-onepage__dialog" closeDisabled={cancel.isPending} open={closeOpen} onClose={() => { if (!cancel.isPending) setCloseOpen(false); }} title="가입을 취소할까요?" description="현재 작성되던 정보가 사라집니다. 가입을 취소하고 원래 화면으로 돌아가시겠습니까?" primaryAction={<Button variant="danger" isLoading={cancel.isPending} onClick={() => cancel.mutate()}>종료</Button>} secondaryAction={<Button variant="secondary" disabled={cancel.isPending} onClick={() => setCloseOpen(false)}>취소</Button>}>{cancel.isError && <Alert persistent variant="danger" title="가입을 취소하지 못했습니다">{error}</Alert>}</Dialog>
      <Dialog className="partner-onepage__dialog partner-onepage__dialog--terms" open={Boolean(openTerm)} onClose={() => setOpenTerm(undefined)} title={openTerm?.title ?? "약관 상세"} description={openTerm ? `${openTerm.required ? "필수" : "선택"} 약관` : undefined} primaryAction={<Button onClick={() => setOpenTerm(undefined)}>닫기</Button>}><div className="partner-flow__term" ref={termContentRef}>{openTerm?.content}</div></Dialog>
    </main>
  );
}
